import type { ApprovedImageStatus } from "./types";
import type { ControlSnapshot } from "./controlSnapshot";

// Operational progress is weighted, not a clock: never extrapolate an ETA from it.
export function updateTiming(snapshot: ControlSnapshot, images: ApprovedImageStatus) {
  const run = snapshot.data?.run;
  const terminal = run && ["succeeded", "failed", "cancelled"].includes(run.status);
  const start = Date.parse(run?.started_at ?? "");
  // Never subtract a server timestamp from the browser's wall clock. Every
  // successful poll carries its own server-time anchor; failed reads retain it.
  // No client extrapolation: background tabs and clock changes cannot inflate it.
  const serverTime = Date.parse(snapshot.data?.server_time ?? "");
  const end = terminal ? Date.parse(run.finished_at ?? run.updated_at) : serverTime;
  const elapsed = Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, (end - start) / 1000) : null;
  const result = { elapsed, remaining: null as number | null, note: "Aún no hay una estimación fiable." };
  if (snapshot.state !== "ready") return { ...result, note: "Esperando un estado actualizado." };
  if (!run) return { ...result, note: "Sin actualización iniciada." };
  if (run.status === "succeeded") return { ...result, remaining: 0, note: "Actualización terminada." };
  if (terminal) return { ...result, note: "El ciclo está detenido." };
  if (["paused", "pause_requested", "waiting_approval"].includes(run.status)) {
    return { ...result, note: "Disponible cuando el proceso continúe." };
  }
  if (!images.available || !run.change_set_id || run.change_set_id.slice(0, 8) !== images.change_set) return result;
  const age = serverTime - Date.parse(images.updated_at);
  if (!Number.isFinite(age) || age < -60000 || age > 120000) return result;
  const final = ["images", "finalize"].includes(run.phase) && images.workflow.current_phase === "final_reconcile" && images.reconciliation.pass === "gallery"
    && images.reconciliation.state === "running";
  const seconds = final ? images.reconciliation.eta_seconds
    : run.phase === "images" && images.worker.state === "running" ? images.timing.eta_seconds : null;
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return result;
  return { ...result, remaining: seconds, note: final
    ? "Validación final en curso; estimación orientativa."
    : "Sólo imágenes; falta añadir la validación final." };
}
